import { useEffect, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';
import throttle from 'lodash/throttle';
import noop from '../utils/noop';

const useInViewport = (
  ref: RefObject<Element | null>,
  disconnect = false,
  { delay = 500, threshold = 1 }: { delay?: number; threshold?: number }
) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (disconnect) {
      setIsVisible(false);
    }
  }, [disconnect]);

  useLayoutEffect(() => {
    if (disconnect) return noop;

    const throttledSetIsVisible = throttle(setIsVisible, delay, {
      leading: false,
    });

    const { current } = ref;
    const observer = new IntersectionObserver(([entry]) => throttledSetIsVisible(entry.isIntersecting), {
      threshold,
    });
    observer.observe(current as Element);

    return () => {
      observer.disconnect();
      // In case the target is not visible for the full `delay`
      throttledSetIsVisible((currVal) => currVal);
    };
  }, [delay, disconnect, ref, threshold]);

  return isVisible;
};

export default useInViewport;
